// Earned nickname colours. Purely cosmetic — the server validates that a
// requested colour is actually unlocked by the caller's stats before writing
// it to the public player doc, so nobody can force a colour they haven't
// earned. Thresholds here MUST match the validator in
// functions/src/callable/rooms.ts (unlockThreshold).

export type NameColorId =
  | 'default'
  | 'cyan'
  | 'magenta'
  | 'violet'
  | 'lime'
  | 'amber'
  | 'gold';

export interface NameColor {
  id: NameColorId;
  color: string;
  /** Minimum lifetime stats to unlock; omitted fields mean "no requirement". */
  unlock: { games?: number; wins?: number; mvp?: number };
}

// Nick colours are earned by playing — never sold. The shop is cosmetics you
// buy (frames, card backs); colours stay an achievement so they mean something.
export const NAME_COLORS: NameColor[] = [
  { id: 'default', color: '#e8f4ff', unlock: {} },
  { id: 'cyan', color: '#00e5ff', unlock: {} },
  { id: 'magenta', color: '#ff2e88', unlock: {} },
  { id: 'violet', color: '#8a5cff', unlock: {} },
  { id: 'lime', color: '#c6ff4d', unlock: { games: 3 } },
  { id: 'amber', color: '#ffb84d', unlock: { wins: 5 } },
  { id: 'gold', color: '#ffd700', unlock: { mvp: 10 } },
];

export const DEFAULT_NAME_COLOR: NameColorId = 'default';

const byId = new Map(NAME_COLORS.map((c) => [c.id, c]));

/** Hex for a stored colour id; the neutral default for unknown/missing ids. */
export function colorForId(id?: string | null): string {
  return byId.get((id as NameColorId) ?? 'default')?.color ?? '#e8f4ff';
}

export interface UnlockStats {
  games: number;
  wins: number;
  mvp: number;
}

export function isUnlocked(c: NameColor, s: UnlockStats): boolean {
  return (
    (c.unlock.games ?? 0) <= s.games &&
    (c.unlock.wins ?? 0) <= s.wins &&
    (c.unlock.mvp ?? 0) <= s.mvp
  );
}

/** Short "how to unlock" hint, or null when already free. */
export function unlockHint(c: NameColor): string | null {
  if (c.unlock.mvp) return `${c.unlock.mvp} MVP`;
  if (c.unlock.wins) return `${c.unlock.wins}W`;
  if (c.unlock.games) return `${c.unlock.games}G`;
  return null;
}

const KEY = 'neonfall.nameColor';

export function savedNameColor(): NameColorId {
  const v = localStorage.getItem(KEY);
  return v && byId.has(v as NameColorId) ? (v as NameColorId) : DEFAULT_NAME_COLOR;
}

/**
 * The raw stored id, or null if this browser never picked one — unlike
 * savedNameColor(), which falls back to 'default' and so can't tell "never
 * chosen" apart from "explicitly chose default". Used only to decide whether
 * a pre-existing local pick needs migrating up to the wallet doc.
 */
export function hasSavedNameColor(): string | null {
  const v = localStorage.getItem(KEY);
  return v && byId.has(v as NameColorId) && v !== DEFAULT_NAME_COLOR ? v : null;
}

export function setSavedNameColor(id: NameColorId): void {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* private mode — colour just won't persist */
  }
}
